// Cosmetics (Design §11.2): how each catalog item looks. The server only knows ids, kinds and
// prices (`server::looks`); ids never change. Purely visual, nothing here touches the game.
import { type Parts, REACTIVE_TILES, STANDARD_PARTS, TILES_PER_LAYER, drawTile, drawTurret, muzzles } from './parts.js';
import type { SfxName } from './sfx.js';
import { sound } from './sound.js';

/** Kind = index into a tank's looks (`protocol::Looks`). */
export const PAINT = 0;
export const BULLET = 1;
export const DESTRUCTION = 2;
export const TRACKS = 3;
export const MUZZLE = 4;
export const POSE = 5;
/** Multi-coloured hull or a flag (`colors.ts`); the shop shows it first. */
export const COLOR = 6;
export const LOOK_SLOTS = 7;
/** Emote packs (Design §11.4): bought, not worn; no slot of the looks. */
export const EMOTES = 7;
/** The database's kinds of the emote keys 1–4 (their item: an emote id). */
export const EMOTE_KEYS = 8;
export const KIND_NAMES = ['Paint', 'Bullet', 'Explosion', 'Track marks', 'Muzzle flash', 'Victory pose', 'Color', 'Emotes'];
/** Tabs of the cosmetics shop, in this order. */
export const SHOP_KINDS = [COLOR, PAINT, TRACKS, DESTRUCTION, MUZZLE, BULLET, POSE, EMOTES];
const SNOWBALL = 30;
/** Bullet looks drawn instead of the plain bullet (Herzchen, Sternchen, Schneeball): no dot on top. */
export const REPLACES_BULLET = new Set([21, 22, SNOWBALL]);

const DARK = 'rgba(0, 0, 0, 0.3)';
const LIGHT = 'rgba(255, 255, 255, 0.35)';
const TRAIL_POINTS = 12;
const TRAIL_SPACING = 3; // world units between trail points
const SPARK_MS = 450; // life of a shooting-star spark
const SPARKS = ['#ffffff', '#fff3b0', '#ffd34d'];
const SNOW_EDGE = '#5f7b98';
export const CONFETTI = ['#e6194b', '#ffe119', '#3cb4ff', '#3cd44b', '#f032e6', '#f58231'];
const TRACK_MS = 1500; // a print fades out over this long
const SINK_MS = 1500; // a tank sinking in open water is gone after this long
const TRACK_SPACING = 12; // world units driven between prints
const TRACK_MAX = 40; // prints per tank
const TRACK_SIDE = 0.65; // track centres at ± this many tank radii
const LEUCHTSPUR = 34;
const SHARD = 64; // bullet flag: splinter of a shrapnel shot, not fired from a barrel
/** Bullet flags (`sim::game::BULLET_*`) of those that end in a blast or on a tank: no puff. */
const BLASTING = 1 | 16 | 128; // explosive, stuck, sticky
const PUFF_MS = 380; // a bullet that just runs out puffs away this long
const PUFF_REACH = 30; // gone this close to a tank: it hit, no puff
const FLASH_FOLLOW_MS = 120; // a flash sits on the muzzle this long, then stays where it is
const FLASH_DEDUPE_MS = 30; // bullets of one tank this close together share a flash (scatter, twin)
/** How long each muzzle flash lasts; standard has none. */
const FLASH_MS: Record<number, number> = { 39: 250, 40: 250, 41: 250, 42: 500, 43: 300, 44: 250, 45: 400, 46: 400, 47: 200, 275: 450 };
const FLASH_MAX_MS = Math.max(...Object.values(FLASH_MS));

/** `#rrggbb` with an alpha 0..1. */
function alpha(hex: string, a: number): string {
  return hex + Math.round(a * 255).toString(16).padStart(2, '0');
}

/** `#rrggbb` darkened (`f` < 1) as `rgb(…)`. */
function shade(hex: string, f: number): string {
  const v = parseInt(hex.slice(1, 7), 16);
  return `rgb(${((v >> 16) & 255) * f}, ${((v >> 8) & 255) * f}, ${(v & 255) * f})`;
}

/** Same pseudo-random 0..1 for the same inputs, so patterns stay put between frames. */
export function hash(a: number, b: number): number {
  const v = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

/** Path of a 5-pointed star around x, y. */
function starPath(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot: number): void {
  ctx.beginPath();
  for (let k = 0; k < 10; k++) {
    const [a, d] = [rot + (k * Math.PI) / 5 - Math.PI / 2, k % 2 ? size * 0.45 : size];
    ctx.lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
  }
  ctx.closePath();
}

/** Path of a heart around x, y, `size` about its half width. */
function heartPath(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot: number): void {
  const [c, s] = [Math.cos(rot) * size, Math.sin(rot) * size];
  const at = (u: number, v: number): [number, number] => [x + u * c - v * s, y + u * s + v * c];
  ctx.beginPath();
  ctx.moveTo(...at(0, 0.8));
  ctx.bezierCurveTo(...at(-1.3, -0.1), ...at(-0.6, -1.1), ...at(0, -0.45));
  ctx.bezierCurveTo(...at(0.6, -1.1), ...at(1.3, -0.1), ...at(0, 0.8));
  ctx.closePath();
}

/** Outline of a skull of half-width `size` around the origin, upright: round dome, narrower jaw. */
export function skullPath(ctx: CanvasRenderingContext2D, size: number): void {
  const s = size;
  ctx.beginPath();
  ctx.moveTo(-s * 0.45, s * 0.85);
  ctx.lineTo(-s * 0.45, s * 0.5);
  ctx.bezierCurveTo(-s * 1.05, s * 0.35, -s * 1.05, -s * 0.95, 0, -s * 0.95);
  ctx.bezierCurveTo(s * 1.05, -s * 0.95, s * 1.05, s * 0.35, s * 0.45, s * 0.5);
  ctx.lineTo(s * 0.45, s * 0.85);
  ctx.quadraticCurveTo(0, s * 1.0, -s * 0.45, s * 0.85);
  ctx.closePath();
}

/**
 * Pattern on hull and tracks (tank space, x = forward): about ±r × ±0.85 r, clipped to the
 * subpaths `body` adds (the shapes of the tank's parts). Call after filling them in the player
 * colour; `angle` is the tank's angle in the world (Chrom's shine stays with the light).
 */
export function paintBody(
  ctx: CanvasRenderingContext2D,
  id: number,
  r: number,
  color: string,
  angle: number,
  body: (ctx: CanvasRenderingContext2D) => void,
  colored = false, // over a colour look: only lighten and darken it
): void {
  if (!id) return;
  const [w, h] = [r * 2, r * 1.7]; // whole body
  ctx.save();
  ctx.beginPath();
  body(ctx);
  ctx.clip();
  switch (id) {
    case 1: // Streifen: two racing stripes along the hull, one along each track
      ctx.fillStyle = LIGHT;
      ctx.fillRect(-r, -r * 0.28, w, r * 0.16);
      ctx.fillRect(-r, r * 0.12, w, r * 0.16);
      ctx.fillRect(-r, -r * 0.7, w, r * 0.1);
      ctx.fillRect(-r, r * 0.6, w, r * 0.1);
      break;
    case 2: // Tarnfleck
      for (const [x, y, s, dark] of [
        [-0.6, -0.25, 0.3, 1], [-0.15, 0.25, 0.32, 0], [0.45, -0.2, 0.28, 1],
        [0.7, 0.3, 0.22, 0], [-0.75, 0.35, 0.2, 0], [0.1, -0.4, 0.2, 0],
        [-0.45, -0.7, 0.22, 0], [0.35, 0.68, 0.24, 1], [0.85, -0.65, 0.2, 1], [-0.85, 0.7, 0.2, 1],
      ]) {
        ctx.beginPath();
        ctx.ellipse(x * r, y * r, s * r * 1.3, s * r, x + y, 0, Math.PI * 2);
        ctx.fillStyle = dark ? DARK : LIGHT;
        ctx.fill();
      }
      break;
    case 3: { // Karo, aligned with the hull edges
      const s = r / 3;
      const top = -r * 0.5 - 2 * s;
      ctx.fillStyle = DARK;
      for (let i = 0; -r + i * s < r; i++) {
        for (let j = 0; j < 7; j++) if ((i + j) % 2) ctx.fillRect(-r + i * s, top + j * s, s, s);
      }
      break;
    }
    case 4: // Flammen: tongues from the rear, licking forward; the outer ones in the plain colour on dark
      for (const [color, len] of [[DARK, 1], [LIGHT, 0.65]] as const) {
        ctx.beginPath();
        if (color === DARK) ctx.rect(-r, -h / 2, w, h); // with 'evenodd': all but the tongues
        ctx.moveTo(-r, -h / 2);
        for (const y of [-0.4, -0.2, 0, 0.2, 0.4]) {
          ctx.quadraticCurveTo(-r + w * len * 0.35, (y - 0.13) * h, -r + w * len * (0.75 - Math.abs(y) * 0.8), y * h);
          ctx.quadraticCurveTo(-r + w * len * 0.25, (y + 0.07) * h, -r, (y + 0.1) * h);
        }
        ctx.lineTo(-r, h / 2);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill('evenodd');
      }
      break;
    case 11: // Wirbel: four spiral arms around the turret
      ctx.lineWidth = r * 0.16;
      ctx.lineCap = 'round';
      for (let k = 0; k < 4; k++) {
        ctx.beginPath();
        for (let t = 0; t <= 1; t += 0.05) {
          const a = (k * Math.PI) / 2 + t * Math.PI * 2.2;
          const d = r * (0.15 + t * 1.1);
          ctx.lineTo(Math.cos(a) * d, Math.sin(a) * d);
        }
        ctx.strokeStyle = k % 2 ? DARK : LIGHT;
        ctx.stroke();
      }
      break;
    case 14: // Zebra: dark stripes swinging across hull and tracks
      ctx.lineWidth = r * 0.17;
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
      for (const x of [-0.8, -0.4, 0, 0.4, 0.8]) {
        ctx.beginPath();
        ctx.moveTo(x * r, -h / 2);
        ctx.bezierCurveTo((x + 0.3) * r, -h / 4, (x - 0.3) * r, h / 4, x * r, h / 2);
        ctx.stroke();
      }
      break;
    case 15: { // Pixel-Tarn: a grid of squares in darker and lighter tones
      const s = r / 4;
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 7; j++) {
          const v = hash(i, j);
          if (v > 0.6) continue;
          ctx.fillStyle = v < 0.2 ? 'rgba(0, 0, 0, 0.4)' : v < 0.4 ? DARK : LIGHT;
          ctx.fillRect(-r + i * s, -h / 2 + j * s, s, s);
        }
      }
      break;
    }
    case 16: { // Chrom: a gloss plus a shine band that moves as the tank turns
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fillRect(-r, -h / 2, w, h);
      const at = Math.sin(angle) * r; // along the diagonal
      const shine = ctx.createLinearGradient(at - r * 0.4, at - r * 0.4, at + r * 0.4, at + r * 0.4);
      shine.addColorStop(0, 'rgba(255, 255, 255, 0)');
      shine.addColorStop(0.5, 'rgba(255, 255, 255, 0.75)');
      shine.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = shine;
      ctx.fillRect(-r, -h / 2, w, h);
      break;
    }
    case 17: { // Neon-Kanten: dark body, glowing edges in the player colour (over a colour look: half dark, white glow)
      ctx.fillStyle = colored ? 'rgba(10, 10, 20, 0.5)' : 'rgba(10, 10, 20, 0.88)';
      ctx.fillRect(-r, -h / 2, w, h);
      const glow = colored ? 'rgba(255, 255, 255, 0.9)' : color;
      ctx.strokeStyle = glow;
      ctx.shadowColor = glow;
      ctx.shadowBlur = 8; // canvas pixels, not scaled
      ctx.lineWidth = r * 0.1;
      ctx.strokeRect(-r * 0.85 + 2.5, -r * 0.5 + 2.5, r * 1.7 - 5, r - 5);
      ctx.strokeRect(-r + 2, -r * 0.85 + 2, w - 4, r * 0.4 - 4);
      ctx.strokeRect(-r + 2, r * 0.45 + 2, w - 4, r * 0.4 - 4);
      break;
    }
    case 18: // Sterne: scattered stars and dots
      for (const [x, y, s, star] of [
        [-0.55, -0.2, 0.2, 1], [0.15, 0.2, 0.22, 1], [0.6, -0.25, 0.16, 1], [-0.2, -0.3, 0.07, 0],
        [0.45, 0.3, 0.07, 0], [-0.6, 0.3, 0.08, 0], [-0.7, -0.65, 0.12, 1], [0.1, -0.65, 0.07, 0],
        [0.7, -0.65, 0.12, 1], [-0.3, 0.65, 0.12, 1], [0.5, 0.65, 0.07, 0], [-0.85, 0.65, 0.07, 0],
      ]) {
        if (star) starPath(ctx, x * r, y * r, s * r, x * 3);
        else {
          ctx.beginPath();
          ctx.arc(x * r, y * r, s * r, 0, Math.PI * 2);
        }
        ctx.fillStyle = star ? 'rgba(255, 255, 255, 0.6)' : DARK;
        ctx.fill();
      }
      break;
  }
  ctx.restore();
}

interface Trail {
  /** x, y, newest last. */
  points: number[];
  /** Bullet flags when last seen. */
  flags: number;
}

interface Spark {
  x: number;
  y: number;
  /** World units per ms. */
  vx: number;
  vy: number;
  start: number;
  color: string;
}

interface Burst {
  id: number;
  x: number;
  y: number;
  /** Tank angle when it died (Turmflug). */
  angle: number;
  color: string;
  start: number;
  /** Per particle: angle, speed, spin/colour seed; reactive tiles: which ones fly off. */
  parts: number[];
}

/** Not a destruction: a layer of reactive tiles falling off at a blocked hit. */
const TILES = 100;
const BURST_MS: Record<number, number> = {
  0: 350, 8: 700, 9: 1300, 10: 750, 13: 900, 24: 1000, 25: 350, 26: 1500, 27: 1100, 28: 1600, 29: 2400,
  [TILES]: 600,
};
/** Particles per destruction; Pixel-Zerfall: one per cell of a 5 × 4 grid over the tank. */
const BURST_PARTS: Record<number, number> = { 8: 18, 9: 36, 13: 1, 24: 20, 26: 3, 27: 14 };
const PIXEL_GRID = [5, 4];
const SMOKE_PUFFS = 6;
const SMOKE_MS = 1600; // one puff from the wreck to gone
const WRECK_FADE_MS = 2000; // warm-up: the wreck fades out until the tank is back in

/** One print of the tracks on the ground. */
interface Print {
  x: number;
  y: number;
  angle: number;
  start: number;
  /** Counts the prints of a tank; consecutive numbers are joined (Leuchtspur). */
  seq: number;
  /** 0..1, hover tanks leave fainter prints. */
  alpha: number;
}

/** Muzzle flash of one shot, at the muzzle(s) of tank `tank`. */
interface Flash {
  tank: number;
  x: number;
  y: number;
  angle: number;
  /** The firing muzzle: distance forward and sideways offset, see `muzzles`. */
  tip: number;
  side: number;
  start: number;
  /** Counts the flashes, varies the random patterns. */
  seq: number;
  /** The bullet fired and where it is now (where it was last seen once gone), for Plasma. */
  bullet: number;
  bx: number;
  by: number;
}

/** Smoking wreck (Rauchsäule), until its tank lives again. */
interface Smoke {
  x: number;
  y: number;
  start: number;
}

/** Wreck left where a tank died in the warm-up, which respawns it in the same tick. */
interface Wreck extends Smoke {
  tank: number;
  angle: number;
  turret: boolean;
  smoke: boolean;
}

/**
 * State the looks need across frames: recent bullet positions for trails, and who was alive
 * last frame, so a tank's destruction plays once when it dies.
 */
/**
 * Rocking of tanks on tracks: kick per unit/s of speed change, spring (1/s) and damping, and a
 * jump in one frame that means a new position, not driving.
 */
const ROCK_KICK = 0.5;
const ROCK_SPRING = 16;
const ROCK_DAMPING = 0.45;
const ROCK_JUMP = 40;
/** Seconds the measured speed is smoothed over. */
const ROCK_SMOOTH = 0.12;

export class Effects {
  /** `quiet`: no sounds (the previews in the cosmetics shop). */
  constructor(private readonly quiet = false) {}

  private sound(name: SfxName, x: number, y: number): void {
    if (!this.quiet) sound(name, x, y);
  }

  private trails = new Map<number, Trail>();
  private alive: boolean[] = [];
  /** Reactive layers still on, per tank last frame. */
  private reactive: number[] = [];
  /** Wrecks whose turret flew off (Turmflug), until the tank lives again. */
  private turretless: boolean[] = [];
  /** When a tank went under in open water, while it is dead. */
  private sunk: (number | undefined)[] = [];
  /** Tanks going under in open water, where they went down (in the warm-up they are back at once). */
  private sinks: { tank: number; x: number; y: number; angle: number; start: number }[] = [];
  private smokes: (Smoke | undefined)[] = [];
  private wrecks: Wreck[] = [];
  /** Per tank last frame: respawn count, x, y, angle. */
  private spawned: number[][] = [];
  private bursts: Burst[] = [];
  private sparks: Spark[] = [];
  /** Bullets that ran out without hitting anything (end of range, last bounce, hedge, hill). */
  private puffs: { x: number; y: number; start: number }[] = [];
  private prints: Print[][] = [];
  /** Where each tank laid its last print (or started driving). */
  private printedAt: (Print | undefined)[] = [];
  private flashes: Flash[] = [];
  /** Bullet ids already seen, so each shot flashes once. */
  private fired = new Set<number>();
  private flashSeq = 0;
  private last = 0;
  /** Per tank: last x, y, speed along the hull, and the rocking (tilt, its speed). */
  private rocking: number[][] = [];

  /** How far tank `i` tips forward (stopping, > 0) or back (driving off), −1..1. */
  tilt(i: number): number {
    return this.rocking[i]?.[3] ?? 0;
  }

  /** False while the wreck of tank `i` has lost its turret. */
  hasTurret(i: number): boolean {
    return !this.turretless[i];
  }

  /** Warm-up wrecks still fading out, `fade` 1 → 0. */
  fadingWrecks(now: number): (Wreck & { fade: number })[] {
    return this.wrecks.map((w) => ({ ...w, fade: Math.max(0, 1 - (now - w.start) / WRECK_FADE_MS) }));
  }

  /**
   * `tanks`/`bullets` as in a render frame (bullet stride: id, x, y, flags, owner),
   * `looks` `LOOK_SLOTS` per tank (missing = standard), `now` in ms.
   */
  update(
    tanks: Float32Array,
    tankStride: number,
    bullets: Float32Array,
    bulletStride: number,
    looks: Uint16Array | undefined,
    colors: (i: number) => string,
    now: number,
    inWater: (x: number, y: number) => boolean = () => false,
  ): void {
    const dt = this.last ? Math.min(now - this.last, 100) : 0;
    this.last = now;
    const seen = new Set<number>();
    for (let i = 0; i < bullets.length; i += bulletStride) {
      const id = bullets[i];
      seen.add(id);
      let trail = this.trails.get(id);
      if (!trail) this.trails.set(id, (trail = { points: [], flags: 0 }));
      trail.flags = bullets[i + 3];
      const p = trail.points;
      const [x, y] = [bullets[i + 1], bullets[i + 2]];
      if (p.length === 0 || Math.hypot(x - p[p.length - 2], y - p[p.length - 1]) >= TRAIL_SPACING) {
        p.push(x, y);
        if (p.length > TRAIL_POINTS * 2) p.splice(0, 2);
      }
      if (looks?.[bullets[i + 4] * LOOK_SLOTS + BULLET] === 12 && p.length >= 4) this.spray(p, dt, now);
    }
    this.sparks = this.sparks.filter((s) => now - s.start < SPARK_MS);
    this.puffs = this.puffs.filter((p) => now - p.start < PUFF_MS);
    const gone = [...this.trails.keys()].filter((id) => !seen.has(id));
    // All at once with none left: a new round, not bullets running out.
    const cleared = bullets.length === 0 && gone.length > 3;
    for (const id of gone) {
      const { points: p, flags } = this.trails.get(id)!;
      this.trails.delete(id);
      if (cleared || p.length < 2 || (flags & BLASTING) !== 0) continue;
      const [x, y] = [p[p.length - 2], p[p.length - 1]];
      let hit = false;
      for (let o = 0; o < tanks.length && !hit; o += tankStride) hit = Math.hypot(tanks[o] - x, tanks[o + 1] - y) < PUFF_REACH;
      if (!hit) this.puffs.push({ x, y, start: now });
    }

    // A tank's centre last frame may be just short of the water it died in this tick.
    const nearWater = (x: number, y: number) =>
      inWater(x, y) || [0, 1, 2, 3, 4, 5, 6, 7].some((k) => inWater(x + 6 * Math.cos((k * Math.PI) / 4), y + 6 * Math.sin((k * Math.PI) / 4)));
    const n = tanks.length / tankStride;
    if (this.alive.length !== n) [this.alive, this.turretless, this.smokes, this.reactive, this.spawned] = [[], [], [], [], []];
    for (let i = 0; i < n; i++) {
      const o = i * tankStride;
      const alive = tanks[o + 4] === 1;
      const [respawns, x0, y0, a0] = this.spawned[i] ?? [];
      if (this.alive[i] && respawns !== undefined && tanks[o + 20] !== respawns && nearWater(x0, y0)) {
        // Died in open water and respawned in one tick (warm-up): it sinks where it was.
        this.sinks.push({ tank: i, x: x0, y: y0, angle: a0, start: now });
        this.sound('splash', x0, y0);
      } else if (this.alive[i] && respawns !== undefined && tanks[o + 20] !== respawns) {
        // Died and respawned in one tick (warm-up): the death plays where it was last frame.
        const id = looks?.[i * LOOK_SLOTS + DESTRUCTION] ?? 0;
        this.burst(id, x0, y0, a0, colors(i), now);
        this.sound('destroyed', x0, y0);
        this.wrecks.push({ tank: i, x: x0, y: y0, angle: a0, start: now, turret: id !== 13, smoke: id === 25 });
        // Its muzzle flashes stay with the wreck: following the tank they would stretch to
        // where it is back (Plasma's bolt across the whole map).
        this.flashes = this.flashes.filter((f) => f.tank !== i);
      }
      this.spawned[i] = [tanks[o + 20], tanks[o], tanks[o + 1], tanks[o + 2]];
      if (this.alive[i] && !alive && inWater(tanks[o], tanks[o + 1])) {
        // Went under in open water: it sinks instead of blowing up.
        this.sunk[i] = now;
        this.sinks.push({ tank: i, x: tanks[o], y: tanks[o + 1], angle: tanks[o + 2], start: now });
        this.sound('splash', tanks[o], tanks[o + 1]);
      } else if (this.alive[i] && !alive) {
        const id = looks?.[i * LOOK_SLOTS + DESTRUCTION] ?? 0;
        this.burst(id, tanks[o], tanks[o + 1], tanks[o + 2], colors(i), now);
        this.sound('destroyed', tanks[o], tanks[o + 1]);
        this.turretless[i] = id === 13;
        this.smokes[i] = id === 25 ? { x: tanks[o], y: tanks[o + 1], start: now } : undefined;
      }
      if (alive) [this.turretless[i], this.smokes[i], this.sunk[i]] = [false, undefined, undefined];
      this.alive[i] = alive;
      const reactive = tanks[o + 19];
      if (reactive < this.reactive[i] && alive) {
        // Layers from the front: with `reactive` left, the tiles before them just flew off.
        const off = REACTIVE_TILES.length - reactive * TILES_PER_LAYER;
        const parts = [...REACTIVE_TILES.keys()].slice(off - (this.reactive[i] - reactive) * TILES_PER_LAYER, off);
        this.bursts.push({ id: TILES, x: tanks[o], y: tanks[o + 1], angle: tanks[o + 2], color: '', start: now, parts });
        this.sound('blocked', tanks[o], tanks[o + 1]);
      }
      this.reactive[i] = reactive;
      this.rock(i, tanks[o], tanks[o + 1], tanks[o + 2], alive, dt);
    }
    this.bursts = this.bursts.filter((b) => now - b.start < (BURST_MS[b.id] ?? BURST_MS[0]));
    this.wrecks = this.wrecks.filter((w) => now - w.start < WRECK_FADE_MS);
    this.sinks = this.sinks.filter((k) => now - k.start < SINK_MS);
  }

  /** Tanks going under now, where they went down, with the share 0..1 of the way. */
  sinkings(now: number): { tank: number; x: number; y: number; angle: number; share: number }[] {
    return this.sinks.map((k) => ({ ...k, share: (now - k.start) / SINK_MS }));
  }

  /** How far dead tank `i` has sunk, 0..1 (≥ 1: gone); undefined if it didn't go under. */
  sinking(i: number, now: number): number | undefined {
    const start = this.sunk[i];
    return start === undefined ? undefined : (now - start) / SINK_MS;
  }

  /**
   * Lays track prints behind the tanks that wear tracks, one per `TRACK_SPACING` driven;
   * `opacity(i)` 0 = none (dead, cloaked), else how strong.
   */
  layTracks(
    tanks: Float32Array,
    tankStride: number,
    looks: Uint16Array | undefined,
    opacity: (i: number) => number,
    now: number,
  ): void {
    const n = tanks.length / tankStride;
    if (this.prints.length !== n) [this.prints, this.printedAt] = [Array.from({ length: n }, () => []), []];
    for (let i = 0; i < n; i++) {
      const list = this.prints[i];
      while (list.length && now - list[0].start >= TRACK_MS) list.shift();
      const o = i * tankStride;
      const at = { x: tanks[o], y: tanks[o + 1], angle: tanks[o + 2], start: now, seq: 0, alpha: opacity(i) };
      const last = this.printedAt[i];
      if (!looks?.[i * LOOK_SLOTS + TRACKS] || !at.alpha) {
        this.printedAt[i] = undefined;
        continue;
      }
      const d = last ? Math.hypot(at.x - last.x, at.y - last.y) : Infinity;
      if (d < TRACK_SPACING) continue;
      at.seq = (last?.seq ?? 0) + 1;
      if (d <= TRACK_SPACING * 4) {
        list.push(at);
        if (list.length > TRACK_MAX) list.shift();
      } else at.seq++; // jumped (new round) or just started: a new run
      this.printedAt[i] = at;
    }
  }

  /**
   * Starts a muzzle flash for every new bullet fired near its owner (`loadouts`: 4 bytes per
   * tank, for the muzzles); `visible(i)` false = none (dead, cloaked).
   */
  shots(
    tanks: Float32Array,
    tankStride: number,
    bullets: Float32Array,
    bulletStride: number,
    loadouts: Uint8Array | undefined,
    c: { tankRadius: number; barrelLength: number },
    visible: (i: number) => boolean,
    now: number,
  ): void {
    const n = tanks.length / tankStride;
    const seen = new Map<number, [number, number]>();
    for (let i = 0; i < bullets.length; i += bulletStride) {
      const id = bullets[i];
      seen.set(id, [bullets[i + 1], bullets[i + 2]]);
      if (this.fired.has(id)) continue;
      const owner = bullets[i + 4];
      const o = owner * tankStride;
      if ((bullets[i + 3] & SHARD) !== 0 || owner >= n || !visible(owner)) continue;
      const near = c.barrelLength * 1.35 + c.tankRadius * 2.5;
      if (Math.hypot(bullets[i + 1] - tanks[o], bullets[i + 2] - tanks[o + 1]) > near) continue; // joined late
      if (this.flashes.some((f) => f.tank === owner && now - f.start < FLASH_DEDUPE_MS)) continue;
      const parts: Parts = loadouts?.subarray(owner * 4, owner * 4 + 4) ?? STANDARD_PARTS;
      const [tip, barrels] = muzzles(parts, c.barrelLength);
      // Twin barrels fire one after the other: flash at the one nearer the bullet's side.
      const a = tanks[o + 2];
      const side = -(bullets[i + 1] - tanks[o]) * Math.sin(a) + (bullets[i + 2] - tanks[o + 1]) * Math.cos(a);
      const muzzle = barrels.reduce((best, s) => (Math.abs(s - side) < Math.abs(best - side) ? s : best));
      const [bx, by] = [bullets[i + 1], bullets[i + 2]];
      this.flashes.push({ tank: owner, x: 0, y: 0, angle: 0, tip, side: muzzle, start: now, seq: this.flashSeq++, bullet: id, bx, by });
      this.sound(`shot${parts[0] ?? 0}` as SfxName, tanks[o], tanks[o + 1]); // each cannon its own
    }
    this.fired = new Set(seen.keys());
    this.flashes = this.flashes.filter((f) => {
      const age = now - f.start;
      if (age >= FLASH_MAX_MS) return false; // the look decides at drawing how long it shows
      [f.bx, f.by] = seen.get(f.bullet) ?? [f.bx, f.by];
      if (age >= FLASH_FOLLOW_MS) return true;
      if (f.tank >= n || !visible(f.tank)) return false;
      const o = f.tank * tankStride;
      [f.x, f.y, f.angle] = [tanks[o], tanks[o + 1], tanks[o + 2]];
      return true;
    });
  }

  /** Muzzle flashes, drawn over the tanks and below the bullets (`r` tank radius). */
  drawFlashes(
    ctx: CanvasRenderingContext2D,
    r: number,
    looks: Uint16Array | undefined,
    colors: (i: number) => string,
    now: number,
  ): void {
    for (const f of this.flashes) {
      const id = looks?.[f.tank * LOOK_SLOTS + MUZZLE] ?? 0;
      const ms = FLASH_MS[id];
      if (!ms) continue; // standard: no flash
      const p = (now - f.start) / ms;
      if (p >= 1) continue;
      ctx.save();
      ctx.translate(f.x, f.y);
      ctx.rotate(f.angle);
      ctx.translate(f.tip, f.side);
      muzzleFlash(ctx, id, f, r, colors(f.tank), p);
      ctx.restore();
    }
  }

  /** Track prints on the ground, below everything else on the map. */
  drawTracks(
    ctx: CanvasRenderingContext2D,
    r: number,
    looks: Uint16Array | undefined,
    colors: (i: number) => string,
    now: number,
  ): void {
    ctx.save();
    this.prints.forEach((list, i) => {
      const id = looks?.[i * LOOK_SLOTS + TRACKS] ?? 0;
      if (!id) return;
      const color = colors(i);
      const dark = shade(color, 0.45);
      list.forEach((p, k) => {
        const age = (now - p.start) / TRACK_MS; // 0..1
        ctx.globalAlpha = p.alpha * (1 - age);
        if (id === LEUCHTSPUR) {
          const prev = list[k - 1];
          if (prev?.seq === p.seq - 1) glowLine(ctx, prev, p, r, color, age);
          return;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        trackPrint(ctx, id, p, r, color, dark, age);
        ctx.restore();
      });
    });
    ctx.restore();
  }

  private burst(id: number, x: number, y: number, angle: number, color: string, now: number): void {
    const count = BURST_PARTS[id] ?? 0;
    const parts: number[] = [];
    for (let k = 0; k < count; k++) parts.push(Math.random() * Math.PI * 2, 0.5 + Math.random() * 0.5, Math.random());
    this.bursts.push({ id: BURST_MS[id] ? id : 0, x, y, angle, color, start: now, parts });
  }

  /** Shooting star: sparks sprayed backwards from the head of trail `p`, one per 8 ms. */
  /**
   * A change of speed along the hull kicks the rocking of tank `i`, a damped spring brings it
   * back: driving off tips it back, stopping forward, with a little swing after.
   */
  private rock(i: number, x: number, y: number, angle: number, alive: boolean, dt: number): void {
    const last = this.rocking[i];
    const moved = last ? Math.hypot(x - last[0], y - last[1]) : Infinity;
    if (!alive || !last || dt <= 0 || moved > ROCK_JUMP) {
      this.rocking[i] = [x, y, 0, 0, 0]; // new round, respawn: no kick
      return;
    }
    const s = dt / 1000;
    // Frames and ticks don't line up (a frame may see two ticks or none): smoothed speed.
    const raw = ((x - last[0]) * Math.cos(angle) + (y - last[1]) * Math.sin(angle)) / s;
    const speed = last[2] + (raw - last[2]) * Math.min(1, s / ROCK_SMOOTH);
    let [tilt, swing] = [last[3], last[4]];
    swing -= (speed - last[2]) * ROCK_KICK;
    swing += (-ROCK_SPRING * ROCK_SPRING * tilt - 2 * ROCK_DAMPING * ROCK_SPRING * swing) * s;
    tilt = Math.max(-1, Math.min(1, tilt + swing * s));
    this.rocking[i] = [x, y, speed, tilt, swing];
  }

  private spray(p: number[], dt: number, now: number): void {
    const n = p.length;
    const [x, y] = [p[n - 2], p[n - 1]];
    const len = Math.hypot(x - p[n - 4], y - p[n - 3]) || 1;
    const [dx, dy] = [(x - p[n - 4]) / len, (y - p[n - 3]) / len];
    for (let k = Math.round(dt / 8); k > 0; k--) {
      const back = 20 + Math.random() * 40;
      const side = (Math.random() - 0.5) * 70;
      this.sparks.push({
        x,
        y,
        vx: (-dx * back - dy * side) / 1000,
        vy: (-dy * back + dx * side) / 1000,
        start: now,
        color: SPARKS[Math.floor(Math.random() * SPARKS.length)],
      });
    }
  }

  /**
   * Bullets that ran out puffing away: a little cloud and specks, coloured by `colors(x, y)`
   * (cloud, specks) for the ground there; `seen(x, y)`: in sight.
   */
  drawPuffs(
    ctx: CanvasRenderingContext2D,
    bulletRadius: number,
    now: number,
    seen: (x: number, y: number) => boolean = () => true,
    colors: (x: number, y: number) => [string, string] = () => ['#8c8c84', '#55554f'],
  ): void {
    ctx.save();
    for (const p of this.puffs) {
      if (!seen(p.x, p.y)) continue;
      const [cloud, specks] = colors(p.x, p.y);
      const t = (now - p.start) / PUFF_MS;
      ctx.globalAlpha = 0.55 * (1 - t);
      ctx.fillStyle = cloud;
      ctx.beginPath();
      ctx.arc(p.x, p.y, bulletRadius * (1 + 2.2 * t), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.8 * (1 - t);
      ctx.fillStyle = specks;
      for (let k = 0; k < 5; k++) {
        const a = k * 1.26 + p.x;
        const d = bulletRadius * (1 + 3.5 * t);
        ctx.fillRect(p.x + Math.cos(a) * d - 0.8, p.y + Math.sin(a) * d - 0.8, 1.6, 1.6);
      }
    }
    ctx.restore();
  }

  /** Trails and glow, below the bullets. */
  drawTrails(
    ctx: CanvasRenderingContext2D,
    bullets: Float32Array,
    bulletStride: number,
    bulletRadius: number,
    looks: Uint16Array | undefined,
    colors: (i: number) => string,
    now: number,
  ): void {
    ctx.save();
    for (const s of this.sparks) {
      const age = now - s.start;
      ctx.globalAlpha = 1 - age / SPARK_MS;
      ctx.fillStyle = s.color;
      ctx.fillRect(s.x + s.vx * age - 1, s.y + s.vy * age - 1, 2, 2);
    }
    ctx.globalAlpha = 1;
    ctx.lineCap = 'round';
    for (let i = 0; i < bullets.length; i += bulletStride) {
      const owner = bullets[i + 4];
      const id = looks?.[owner * LOOK_SLOTS + BULLET] ?? 0;
      const color = colors(owner);
      const [x, y] = [bullets[i + 1], bullets[i + 2]];
      if (id === 6 || id === 12 || id === 20) {
        // Leuchten in the player colour; the shooting star's head glows white, the fireball orange.
        const [r, head] =
          id === 6 ? [bulletRadius * 4, color] : [bulletRadius * 3, id === 12 ? '#ffffff' : '#ff9a1f'];
        const glow = ctx.createRadialGradient(x, y, 0, x, y, r);
        glow.addColorStop(0, alpha(head, 0.9));
        glow.addColorStop(1, alpha(head, 0));
        ctx.fillStyle = glow;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
        if (id !== 20) continue;
      }
      if (id === 21 || id === 22) {
        // Herzchen / Sternchen: a spinning shape in the player colour.
        const rot = now / 150 + bullets[i];
        if (id === 21) heartPath(ctx, x, y, bulletRadius * 2.4, rot);
        else starPath(ctx, x, y, bulletRadius * 2.4, rot);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#111';
        ctx.stroke();
        continue;
      }
      if (id === SNOWBALL) {
        // Schneeball: snow flakes left behind, then a shaded white ball.
        const p = this.trails.get(bullets[i])?.points ?? [];
        const flakes = p.length / 2;
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = SNOW_EDGE;
        ctx.lineWidth = 0.6;
        for (let k = 0; k < flakes; k++) {
          const seed = hash(bullets[i], k);
          ctx.globalAlpha = 0.8 * ((k + 1) / flakes);
          ctx.beginPath();
          ctx.arc(p[k * 2] + (seed - 0.5) * 6, p[k * 2 + 1] + (hash(k, bullets[i]) - 0.5) * 6, 0.8 + seed, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        const r = bulletRadius * 1.82;
        const snow = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0, x, y, r);
        snow.addColorStop(0, '#ffffff');
        snow.addColorStop(1, '#a9c3dc');
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = snow;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = SNOW_EDGE;
        ctx.stroke();
        continue;
      }
      if (id !== 5 && id !== 7 && id !== 19 && id !== 20 && id !== 23) continue;
      const p = [...(this.trails.get(bullets[i])?.points ?? []), x, y];
      const segments = p.length / 2 - 1;
      for (let k = 0; k < segments; k++) {
        const fade = (k + 1) / segments; // 1 at the bullet
        const [px, py] = [p[k * 2], p[k * 2 + 1]];
        if (id === 19) {
          // Rauchspur: grey puffs, growing and fading behind the bullet
          ctx.beginPath();
          ctx.arc(px, py, bulletRadius * (1 + 2 * (1 - fade)), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(120, 120, 120, ${0.35 * fade})`;
          ctx.fill();
          continue;
        }
        if (id === 23) {
          // Pixel-Spur: squares on a coarse grid, fading in steps
          const [g, s] = [4, bulletRadius * 1.6];
          ctx.fillStyle = alpha(color, (Math.ceil(fade * 3) / 3) * 0.8);
          ctx.fillRect(Math.round(px / g) * g - s / 2, Math.round(py / g) * g - s / 2, s, s);
          continue;
        }
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(p[k * 2 + 2], p[k * 2 + 3]);
        if (id === 5) {
          ctx.strokeStyle = alpha(color, 0.7 * fade);
          ctx.lineWidth = bulletRadius * 1.4 * fade;
        } else if (id === 7) {
          ctx.strokeStyle = `hsla(${(k * 30 + now / 4) % 360}, 90%, 55%, ${fade})`;
          ctx.lineWidth = bulletRadius * 1.8;
        } else {
          // Feuerball: flickering tail, yellow at the bullet, red at the end
          ctx.strokeStyle = `hsla(${5 + 45 * fade}, 100%, ${45 + 15 * fade}%, ${fade})`;
          ctx.lineWidth = bulletRadius * 2.2 * fade * (0.7 + 0.6 * Math.random());
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** Destruction effects, on top of everything on the map. */
  drawBursts(ctx: CanvasRenderingContext2D, c: { tankRadius: number; barrelLength: number }, now: number): void {
    const r = c.tankRadius;
    ctx.save();
    for (const b of this.bursts) {
      const p = (now - b.start) / BURST_MS[b.id]; // 0..1
      const out = 1 - (1 - p) * (1 - p); // ease out
      if (b.id === 13) {
        // Turmflug: the turret pops up, flies a little way spinning and fades out.
        const [a, speed, seed] = b.parts;
        const d = r * (1 + speed) * out;
        const lift = 1 + 0.5 * Math.sin(Math.PI * p);
        ctx.save();
        ctx.translate(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d);
        ctx.rotate(b.angle + (seed - 0.5) * 8 * out);
        ctx.scale(lift, lift);
        ctx.globalAlpha = p < 0.6 ? 1 : (1 - p) / 0.4;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#111';
        drawTurret(ctx, r, c.barrelLength, '#555');
        ctx.restore();
      }
      if (b.id === 0 || b.id === 8 || b.id === 13 || b.id === 25) {
        ctx.beginPath();
        ctx.arc(b.x, b.y, r * (0.5 + 1.1 * out), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 190, 60, ${0.8 * (1 - p) * (b.id ? 0.6 : 1)})`;
        ctx.fill();
      }
      if (b.id === 8) {
        ctx.lineCap = 'round';
        ctx.lineWidth = 2;
        ctx.strokeStyle = `rgba(255, 220, 80, ${1 - p})`;
        for (let k = 0; k < b.parts.length; k += 3) {
          const [a, speed] = [b.parts[k], b.parts[k + 1]];
          const d = r * (0.6 + 4 * speed * out);
          ctx.beginPath();
          ctx.moveTo(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d);
          ctx.lineTo(b.x + Math.cos(a) * (d - r * 0.6), b.y + Math.sin(a) * (d - r * 0.6));
          ctx.stroke();
        }
      }
      if (b.id === 9) {
        ctx.globalAlpha = p < 0.7 ? 1 : (1 - p) / 0.3;
        for (let k = 0; k < b.parts.length; k += 3) {
          const [a, speed, seed] = [b.parts[k], b.parts[k + 1], b.parts[k + 2]];
          const d = r * 3.5 * speed * out;
          ctx.save();
          ctx.translate(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d);
          ctx.rotate(seed * 10 + p * 12 * (seed - 0.5));
          ctx.fillStyle = CONFETTI[Math.floor(seed * CONFETTI.length)];
          ctx.fillRect(-3, -1.5, 6, 3);
          ctx.restore();
        }
        ctx.globalAlpha = 1;
      }
      if (b.id === 10) {
        for (const [delay, color] of [[0, '#ffffff'], [0.2, b.color]] as const) {
          const q = (p - delay) / (1 - delay);
          if (q <= 0) continue;
          ctx.beginPath();
          ctx.arc(b.x, b.y, r * (0.8 + 4.5 * (1 - (1 - q) * (1 - q))), 0, Math.PI * 2);
          ctx.strokeStyle = alpha(color, 1 - q);
          ctx.lineWidth = 5 * (1 - q) + 1;
          ctx.stroke();
        }
      }
      if (b.id === 24) this.pixels(ctx, b, r, p, out);
      if (b.id === 26) this.fireworks(ctx, b, r, p);
      if (b.id === 27) this.blackHole(ctx, b, r, p);
      if (b.id === 28) {
        // Geist: a see-through tank floats up, swaying, and fades.
        const lift = 1 + 0.3 * p;
        ctx.save();
        ctx.translate(b.x + Math.sin(p * 9) * r * 0.25, b.y - r * 2.2 * out);
        ctx.rotate(b.angle);
        ctx.scale(lift, lift);
        ctx.globalAlpha = 0.55 * (1 - p);
        ctx.fillStyle = b.color;
        ctx.fillRect(-r, -r * 0.85, r * 2, r * 1.7);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.fillRect(-r * 0.85, -r * 0.5, r * 1.7, r);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        drawTurret(ctx, r, c.barrelLength, b.color);
        ctx.restore();
      }
      if (b.id === 29) this.flare(ctx, b, r, p);
      if (b.id === TILES) tiles(ctx, b, r, p);
    }
    for (const s of this.smokes) if (s) smoke(ctx, s, r, now);
    for (const w of this.fadingWrecks(now)) {
      if (!w.smoke) continue;
      ctx.globalAlpha = w.fade;
      smoke(ctx, w, r, now);
    }
    ctx.restore();
  }

  /** Pixel-Zerfall: the tank falls apart into squares in the player colour that drift off. */
  private pixels(ctx: CanvasRenderingContext2D, b: Burst, r: number, p: number, out: number): void {
    const [cols, rows] = PIXEL_GRID;
    const [cw, ch] = [(r * 2) / cols, (r * 1.7) / rows];
    const [cos, sin] = [Math.cos(b.angle), Math.sin(b.angle)];
    ctx.globalAlpha = 1 - p * p;
    for (let k = 0; k < b.parts.length / 3; k++) {
      const [speed, seed] = [b.parts[k * 3 + 1], b.parts[k * 3 + 2]];
      const u = -r + (k % cols) * cw + cw / 2;
      const v = -r * 0.85 + Math.floor(k / cols) * ch + ch / 2;
      const d = 1 + 2.2 * speed * out; // cells fly off away from the middle
      const [x, y] = [b.x + (u * cos - v * sin) * d, b.y + (u * sin + v * cos) * d];
      const s = cw * (1 - 0.6 * p);
      ctx.fillStyle = seed < 0.5 ? b.color : '#555';
      ctx.fillRect(x - s / 2, y - s / 2, s, s);
    }
    ctx.globalAlpha = 1;
  }

  /** Feuerwerk: three rockets climb, then each bursts into a ring of sparks. */
  private fireworks(ctx: CanvasRenderingContext2D, b: Burst, r: number, p: number): void {
    const climb = 0.4;
    for (let k = 0; k < b.parts.length; k += 3) {
      const a = -Math.PI / 2 + (k / 3 - 1) * 0.6 + (b.parts[k] - Math.PI) * 0.05; // fanned upwards
      const [speed, seed] = [b.parts[k + 1], b.parts[k + 2]];
      const top = r * (2 + 1.5 * speed);
      const q = Math.min(p / climb, 1);
      const [x, y] = [b.x + Math.cos(a) * top * q, b.y + Math.sin(a) * top * q];
      if (p < climb) {
        ctx.beginPath();
        ctx.moveTo(x - Math.cos(a) * r * 0.6, y - Math.sin(a) * r * 0.6);
        ctx.lineTo(x, y);
        ctx.strokeStyle = 'rgba(255, 230, 160, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        continue;
      }
      const e = (p - climb) / (1 - climb);
      const spread = r * 1.4 * (1 - (1 - e) * (1 - e));
      ctx.fillStyle = CONFETTI[(Math.floor(b.parts[2] * CONFETTI.length) + (k / 3) * 2) % CONFETTI.length]; // 3 different colours
      ctx.globalAlpha = 1 - e;
      for (let j = 0; j < 12; j++) {
        const t = (j * Math.PI) / 6 + seed;
        ctx.fillRect(x + Math.cos(t) * spread - 1.5, y + Math.sin(t) * spread + r * 0.5 * e * e - 1.5, 3, 3);
      }
      ctx.globalAlpha = 1;
    }
  }

  /** Schwarzes Loch: a dark swirl opens and sucks in debris, then closes. */
  private blackHole(ctx: CanvasRenderingContext2D, b: Burst, r: number, p: number): void {
    const pull = Math.pow(p, 1.5);
    for (let k = 0; k < b.parts.length; k += 3) {
      const [a, speed, seed] = [b.parts[k], b.parts[k + 1], b.parts[k + 2]];
      const d = r * (1.2 + 1.6 * speed) * (1 - pull);
      const t = a + p * 7;
      const s = 4 * (1 - p) + 1;
      ctx.fillStyle = seed < 0.5 ? b.color : '#555';
      ctx.fillRect(b.x + Math.cos(t) * d - s / 2, b.y + Math.sin(t) * d - s / 2, s, s);
    }
    const size = r * 0.9 * Math.min(p / 0.2, 1, (1 - p) / 0.25);
    if (size <= 0) return;
    const hole = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, size);
    hole.addColorStop(0, '#000000');
    hole.addColorStop(0.7, 'rgba(20, 0, 40, 0.9)');
    hole.addColorStop(1, 'rgba(120, 60, 200, 0)');
    ctx.fillStyle = hole;
    ctx.beginPath();
    ctx.arc(b.x, b.y, size, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Flair: a red flare climbs from the wreck on a smoke line, hovers above it, then dies out. */
  private flare(ctx: CanvasRenderingContext2D, b: Burst, r: number, p: number): void {
    const rise = Math.min(p / 0.2, 1);
    const up = 1 - (1 - rise) * (1 - rise);
    const [x, y] = [b.x + r * 0.4 * up + Math.sin(p * 12) * r * 0.05, b.y - r * 2.6 * up + Math.sin(p * 20) * r * 0.05];
    const fade = p < 0.8 ? 1 : (1 - p) / 0.2;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.quadraticCurveTo(b.x, y + r, x, y);
    ctx.strokeStyle = `rgba(200, 200, 200, ${0.5 * fade * (1 - p)})`;
    ctx.lineWidth = 3;
    ctx.stroke();
    const size = r * (0.8 + 0.2 * Math.random());
    const glow = ctx.createRadialGradient(x, y, 0, x, y, size);
    glow.addColorStop(0, `rgba(255, 60, 40, ${0.6 * fade})`);
    glow.addColorStop(1, 'rgba(255, 30, 20, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(x - size, y - size, size * 2, size * 2);
    ctx.beginPath();
    ctx.arc(x, y, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 235, 225, ${fade})`;
    ctx.fill();
  }
}

/** Path of a star with `points` tips around x, y. */
export function spikes(ctx: CanvasRenderingContext2D, x: number, y: number, points: number, outer: number, inner: number, rot: number): void {
  ctx.beginPath();
  for (let k = 0; k < points * 2; k++) {
    const [a, d] = [rot + (k * Math.PI) / points, k % 2 ? inner : outer];
    ctx.lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
  }
  ctx.closePath();
}

/**
 * One muzzle flash in muzzle space (x = forward, origin at the muzzle), `p` 0..1 of its life.
 * Stays within about 0.6 tank radii, so it never hides a bullet (Plasma ends just short of it).
 */
function muzzleFlash(ctx: CanvasRenderingContext2D, id: number, f: Flash, r: number, color: string, p: number): void {
  const fade = 1 - p;
  switch (id) {
    case 39: // Stern: four-pointed white star with a golden rim, popping up and turning
      ctx.globalAlpha = p < 0.6 ? 1 : (1 - p) / 0.4;
      spikes(ctx, r * 0.3, 0, 4, r * 0.6 * Math.min(1, p * 6) * (1 - 0.35 * p), r * 0.14, p * Math.PI * 0.5);
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = '#ffd34d';
      ctx.shadowBlur = 8;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#d9a400';
      ctx.lineWidth = 0.9;
      ctx.stroke();
      break;
    case 40: // Ring: in the player colour, growing and fading
      ctx.beginPath();
      ctx.arc(r * 0.2, 0, r * (0.12 + 0.45 * p), 0, Math.PI * 2);
      ctx.strokeStyle = alpha(color, fade);
      ctx.lineWidth = 1 + 2 * fade;
      ctx.stroke();
      break;
    case 41: // Funken: five sparks spraying forward
      for (let k = 0; k < 5; k++) {
        const a = (k - 2) * 0.3 + (hash(f.seq, k) - 0.5) * 0.2;
        const d = r * 0.6 * Math.sqrt(p);
        ctx.globalAlpha = fade;
        ctx.fillStyle = SPARKS[k % SPARKS.length];
        ctx.fillRect(Math.cos(a) * d - 1, Math.sin(a) * d - 1, 2, 2);
      }
      break;
    case 42: // Rauchring: a grey ring drifting forward
      ctx.beginPath();
      ctx.ellipse(r * 0.9 * p, 0, r * (0.1 + 0.12 * p), r * (0.2 + 0.2 * p), 0, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(150, 150, 150, ${0.85 * fade})`;
      ctx.lineWidth = 2.5 - p;
      ctx.stroke();
      break;
    case 43: { // Comic: "BANG!" in a jagged yellow burst
      const s = r * 0.55 * Math.min(1, p * 5);
      ctx.globalAlpha = p < 0.7 ? 1 : (1 - p) / 0.3;
      spikes(ctx, s * 0.8, 0, 9, s, s * 0.7, f.seq);
      ctx.fillStyle = '#ffe14d';
      ctx.fill();
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.translate(s * 0.8, 0);
      ctx.rotate(-f.angle - 0.15); // words stay readable, a little tilted
      ctx.fillStyle = '#c0161e';
      ctx.font = `bold ${s * 0.5}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('BANG!', 0, 0);
      break;
    }
    case 44: { // Plasma: blue-violet glow, a jagged bolt stretching after the bullet
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.4);
      glow.addColorStop(0, `rgba(230, 220, 255, ${fade})`);
      glow.addColorStop(0.4, `rgba(150, 90, 255, ${0.7 * fade})`);
      glow.addColorStop(1, 'rgba(80, 60, 255, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(-r * 0.4, -r * 0.4, r * 0.8, r * 0.8);
      // How far ahead of the muzzle the bullet is; the bolt ends just short of it.
      const [dx, dy] = [f.bx - f.x, f.by - f.y];
      const len = dx * Math.cos(f.angle) + dy * Math.sin(f.angle) - f.tip - 4;
      if (len <= 0) break;
      const steps = Math.max(2, Math.round(len / 5));
      const flicker = Math.floor(p * 10); // the zigzag changes a few times
      ctx.beginPath();
      ctx.moveTo(0, 0);
      for (let j = 1; j <= steps; j++) {
        const off = j === steps ? 0 : (hash(f.seq + flicker, j) - 0.5) * r * 0.3;
        ctx.lineTo((len * j) / steps, off);
      }
      const bolt = Math.min(1, fade * 2);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = `rgba(150, 90, 255, ${0.5 * bolt})`;
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.strokeStyle = `rgba(200, 230, 255, ${bolt})`;
      ctx.lineWidth = 1.2;
      ctx.stroke();
      break;
    }
    case 45: // Konfetti: six coloured scraps flying forward, tumbling
      ctx.globalAlpha = fade;
      for (let k = 0; k < 6; k++) {
        const seed = hash(f.seq, k);
        const a = (k - 2.5) * 0.28;
        const d = r * (0.3 + 0.35 * seed) * Math.sqrt(p) * 1.6;
        ctx.save();
        ctx.translate(Math.cos(a) * d, Math.sin(a) * d);
        ctx.rotate(seed * 6 + p * 8);
        ctx.fillStyle = CONFETTI[k];
        ctx.fillRect(-1.5, -0.8, 3, 1.6);
        ctx.restore();
      }
      break;
    case 46: { // Herz: a heart in the player colour jumping out of the barrel
      const s = r * 0.28 * Math.min(1, 0.4 + p * 3);
      ctx.globalAlpha = p < 0.6 ? 1 : (1 - p) / 0.4;
      heartPath(ctx, r * 0.15 + r * 0.45 * Math.sin(p * Math.PI * 0.5), 0, s, -f.angle);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      break;
    }
    case 275: { // Totenkopf: a bone-white skull popping out of the barrel, upright, then fading
      const s = r * 0.24 * Math.min(1, 0.4 + p * 4);
      ctx.globalAlpha = p < 0.6 ? 1 : (1 - p) / 0.4;
      ctx.translate(r * 0.15 + r * 0.22 * Math.sin(p * Math.PI * 0.5), 0);
      ctx.rotate(-f.angle); // upright on the map
      skullPath(ctx, s);
      ctx.fillStyle = '#f1ece0';
      ctx.fill();
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      // Eyes, nose and teeth cut out in black.
      ctx.fillStyle = '#111';
      ctx.beginPath();
      ctx.ellipse(-s * 0.36, -s * 0.12, s * 0.24, s * 0.27, 0, 0, Math.PI * 2);
      ctx.moveTo(s * 0.6, -s * 0.12);
      ctx.ellipse(s * 0.36, -s * 0.12, s * 0.24, s * 0.27, 0, 0, Math.PI * 2);
      ctx.moveTo(0, s * 0.18);
      ctx.lineTo(-s * 0.1, s * 0.36);
      ctx.lineTo(s * 0.1, s * 0.36);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      for (const u of [-0.2, 0, 0.2]) {
        ctx.moveTo(s * u, s * 0.56);
        ctx.lineTo(s * u, s * 0.8);
      }
      ctx.lineWidth = 0.6;
      ctx.stroke();
      break;
    }
    case 47: { // Seitenfeuer: a yellow flash, flames blasting out left and right of the muzzle
      const s = r * 0.3 * (1 - 0.3 * p);
      const glow = ctx.createRadialGradient(s * 0.3, 0, 0, s * 0.3, 0, s);
      glow.addColorStop(0, `rgba(255, 250, 210, ${fade})`);
      glow.addColorStop(0.4, `rgba(255, 210, 70, ${0.8 * fade})`);
      glow.addColorStop(1, 'rgba(255, 150, 30, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(s * 0.3, 0, s, 0, Math.PI * 2);
      ctx.fill();
      // Sideways, away from the bullet's path, so the flames may reach further than 0.6 r.
      const len = r * 0.9 * (0.6 + 0.4 * Math.min(1, p * 4));
      for (const side of [-1, 1]) {
        const flame = ctx.createLinearGradient(0, 0, 0, side * len);
        flame.addColorStop(0, `rgba(255, 245, 190, ${fade})`);
        flame.addColorStop(0.6, `rgba(255, 180, 40, ${0.9 * fade})`);
        flame.addColorStop(1, `rgba(240, 90, 20, ${0.3 * fade})`);
        ctx.fillStyle = flame;
        ctx.beginPath();
        ctx.moveTo(-r * 0.2, 0);
        ctx.quadraticCurveTo(-r * 0.32, side * len * 0.6, -r * 0.04, side * len);
        ctx.quadraticCurveTo(r * 0.3, side * len * 0.6, r * 0.2, 0);
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
  }
}

/** Leuchtspur: two lines in the player colour from print `a` to `b`, glowing while new. */
function glowLine(ctx: CanvasRenderingContext2D, a: Print, b: Print, r: number, color: string, age: number): void {
  ctx.lineCap = 'round';
  for (const side of [-TRACK_SIDE, TRACK_SIDE]) {
    const at = (p: Print): [number, number] => [p.x - Math.sin(p.angle) * side * r, p.y + Math.cos(p.angle) * side * r];
    ctx.beginPath();
    ctx.moveTo(...at(a));
    ctx.lineTo(...at(b));
    ctx.strokeStyle = alpha(color, 0.35 * Math.max(0, 1 - age * 3));
    ctx.lineWidth = r * 0.4;
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = r * 0.1;
    ctx.stroke();
  }
}

/** One print in tank space (x = forward), both tracks at y = ± `TRACK_SIDE` r; `age` 0..1. */
function trackPrint(
  ctx: CanvasRenderingContext2D,
  id: number,
  p: Print,
  r: number,
  color: string,
  dark: string,
  age: number,
): void {
  const sides = [-TRACK_SIDE * r, TRACK_SIDE * r];
  const alternate = sides[p.seq % 2]; // Pfoten, Blumen: left, right, left …
  ctx.fillStyle = dark;
  switch (id) {
    case 31: // Profil: cross bars like track links
      for (const y of sides) for (const x of [-3, 3]) ctx.fillRect(x - 1, y - r * 0.2, 2, r * 0.4);
      break;
    case 32: // Punkte
      for (const y of sides) {
        ctx.beginPath();
        ctx.arc(0, y, r * 0.1, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 33: // Pfoten: a pad and three toes in front
      ctx.beginPath();
      ctx.ellipse(0, alternate, r * 0.15, r * 0.13, 0, 0, Math.PI * 2);
      for (const t of [-0.13, 0, 0.13]) {
        ctx.moveTo(r * 0.24, alternate + t * r);
        ctx.arc(r * 0.18, alternate + t * r, r * 0.06, 0, Math.PI * 2);
      }
      ctx.fill();
      break;
    case 35: // Funken: only sparks, drifting off the tracks as they fade
      sides.forEach((y, k) => {
        for (const j of [0, 1]) {
          const seed = hash(p.seq, k * 2 + j);
          ctx.fillStyle = SPARKS[Math.floor(seed * SPARKS.length)];
          ctx.fillRect((j - 0.5) * 6 - age * r * 0.6 - 1, y + (seed - 0.5) * (0.3 + age) * r * 0.8 - 1, 2, 2);
        }
      });
      break;
    case 36: { // Blumen: a flower in the player colour, shrinking and wilting as it fades
      const s = r * 0.17 * (1 - 0.4 * age);
      ctx.fillStyle = age < 0.5 ? color : shade(color, 0.6);
      ctx.beginPath();
      for (let k = 0; k < 5; k++) {
        const a = p.seq + (k * Math.PI * 2) / 5;
        const [x, y] = [Math.cos(a) * s * 0.7, alternate + Math.sin(a) * s * 0.7];
        ctx.moveTo(x + s * 0.5, y);
        ctx.arc(x, y, s * 0.5, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0, alternate, s * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = '#ffd34d';
      ctx.fill();
      break;
    }
    case 37: { // Pixel: squares in two tones
      const light = shade(color, 0.75);
      sides.forEach((y, k) => {
        for (const j of [-1, 0, 1]) {
          ctx.fillStyle = hash(p.seq, k * 3 + j) < 0.5 ? dark : light;
          ctx.fillRect(-1.5, y + j * 3 - 1.5, 3, 3);
        }
      });
      break;
    }
    case 276: // Knochen: each groove across the track a little bone, knobs at both ends
      ctx.beginPath();
      for (const y of sides) {
        const [half, knob] = [r * 0.15, r * 0.065];
        ctx.rect(-r * 0.04, y - half, r * 0.08, 2 * half);
        for (const end of [y - half, y + half]) {
          for (const x of [-knob * 0.8, knob * 0.8]) {
            ctx.moveTo(x + knob, end);
            ctx.arc(x, end, knob, 0, Math.PI * 2);
          }
        }
      }
      ctx.fill();
      break;
    case 277: // Feuerspur: little flames on both tracks, flickering and shrinking, then scorch marks
      sides.forEach((y, k) => {
        if (age > 0.45) {
          // Burnt out: a sooty patch.
          ctx.fillStyle = 'rgba(30, 22, 16, 0.7)';
          ctx.beginPath();
          ctx.ellipse(0, y, r * 0.16, r * 0.11, 0, 0, Math.PI * 2);
          ctx.fill();
          return;
        }
        const burn = 1 - age / 0.45;
        const flicker = hash(p.seq * 2 + k, Math.floor(age * 30)); // changes every ~50 ms
        const s = r * (0.19 + 0.09 * flicker) * (0.45 + 0.55 * burn);
        for (const [color, size] of [['#e8401c', 1], ['#ffa424', 0.7], ['#fff1a0', 0.38]] as const) {
          // A drop of flame, its tip blown back the way the tank came.
          const z = s * size;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(-z * 2.2, y + (flicker - 0.5) * z);
          ctx.quadraticCurveTo(-z * 0.4, y - z * 1.1, z, y);
          ctx.quadraticCurveTo(-z * 0.4, y + z * 1.1, -z * 2.2, y + (flicker - 0.5) * z);
          ctx.fill();
        }
      });
      break;
    case 278: { // Münzen: a gold coin, left, right, left …, glinting while new
      const c = r * 0.15;
      ctx.beginPath();
      ctx.arc(0, alternate, c, 0, Math.PI * 2);
      ctx.fillStyle = '#e2b23a';
      ctx.fill();
      ctx.strokeStyle = '#8a6410';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, alternate, c * 0.62, 0, Math.PI * 2);
      ctx.strokeStyle = '#b8861c';
      ctx.lineWidth = 0.6;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(-c * 0.3, alternate - c * 0.3, c * 0.25, 0, Math.PI * 2);
      ctx.fillStyle = '#fff3b8';
      ctx.fill();
      if (age < 0.3 && hash(p.seq, Math.floor(age * 20)) < 0.6) {
        // A four-pointed glint on the rim.
        spikes(ctx, c * 0.7, alternate - c * 0.7, 4, c * 0.9, c * 0.18, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
      break;
    }
    case 38: // Schneespur: white furrows, edged in the player colour, with little heaps of snow
      sides.forEach((y, k) => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillRect(-TRACK_SPACING / 2, y - r * 0.15, TRACK_SPACING, r * 0.3);
        ctx.fillStyle = dark;
        ctx.fillRect(-TRACK_SPACING / 2, y - r * 0.18, TRACK_SPACING, 1);
        ctx.fillRect(-TRACK_SPACING / 2, y + r * 0.18 - 1, TRACK_SPACING, 1);
        ctx.beginPath();
        ctx.arc((hash(p.seq, k) - 0.5) * TRACK_SPACING, y + Math.sign(y) * r * 0.27, r * 0.07, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = SNOW_EDGE;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      });
      break;
  }
}

/** Reactive tiles fly off the hull, spinning, and fade. */
function tiles(ctx: CanvasRenderingContext2D, b: Burst, r: number, p: number): void {
  const out = 1 + 1.2 * (1 - (1 - p) * (1 - p));
  ctx.save();
  ctx.translate(b.x, b.y);
  ctx.rotate(b.angle);
  ctx.globalAlpha = 1 - p;
  ctx.fillStyle = '#9aa4ad';
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 1;
  REACTIVE_TILES.forEach(([x, y], k) => {
    if (!b.parts.includes(k)) return;
    ctx.save();
    ctx.translate(x * r * out, y * r * out);
    ctx.rotate(p * (k % 2 ? 5 : -5));
    drawTile(ctx, 0, 0, r);
    ctx.restore();
  });
  ctx.restore();
}

/** Rauchsäule: grey puffs rise from the wreck and drift off, fading in after the death. */
function smoke(ctx: CanvasRenderingContext2D, s: Smoke, r: number, now: number): void {
  const age = now - s.start;
  const show = Math.min(age / 600, 1);
  for (let k = 0; k < SMOKE_PUFFS; k++) {
    const q = (age / SMOKE_MS + k / SMOKE_PUFFS) % 1; // 0 = at the wreck
    ctx.beginPath();
    ctx.arc(
      s.x + r * 0.8 * q + Math.sin(q * 6 + k) * r * 0.15,
      s.y - r * 2.4 * q,
      r * (0.25 + 0.45 * q),
      0,
      Math.PI * 2,
    );
    ctx.fillStyle = `rgba(90, 90, 90, ${0.4 * (1 - q) * show})`;
    ctx.fill();
  }
}
